import type { RingSizeData } from '@jouzen/ecom-components';

import type { CurrentRouteValues } from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useCurrentRoute';
import type { ListProduct, Variant } from '@/queries/Products';

const SIZE_LATER = 'size later';

interface GetProductProps {
  currentRoute: CurrentRouteValues;
  productsData: ListProduct[] | undefined;
}

export interface GetProductReturn {
  product: ListProduct | undefined;
  subscription: ListProduct | undefined;
  oneYearMembership: ListProduct | undefined;
  twoYearWarranty: ListProduct | undefined;
  threeYearWarranty: ListProduct | undefined;
  ringSizesData: RingSizeData[] | undefined;
  sizeLaterData: Variant | undefined;
  oneMonthMembershipSku: string;
  oneYearMembershipSku: string;
  twoYearWarrantySku: string;
  threeYearWarrantySku: string;
}

const getProduct = ({
  currentRoute,
  productsData,
}: GetProductProps): GetProductReturn => {
  const product: ListProduct | undefined = productsData?.find((product) => {
    const handle = product.handle;
    return handle === `or4-${currentRoute.finish.value}`;
  });

  const subscription: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) => {
      return handle === 'subscription';
    },
  );

  const oneYearMembership: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'one-year-membership',
  );

  const twoYearWarranty: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'extended-warranty-2year',
  );

  const threeYearWarranty: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'extended-warranty-3year',
  );

  const ringSizesData = product?.variants
    .map((variant) => {
      const size = variant.selectedOptions[0]?.value;
      if (size?.toLowerCase() !== SIZE_LATER) {
        return {
          size,
          sku: variant.sku,
          stockStatus: variant.inventory?.retail,
        };
      }
      return null;
    })
    .filter(Boolean) as RingSizeData[] | undefined;

  const sizeLaterData = product?.variants.find((variant) => {
    const size = variant.selectedOptions[0]?.value;
    return size?.toLowerCase() === SIZE_LATER;
  });

  const oneMonthMembershipSku = subscription?.variants[0]?.sku ?? '';
  const oneYearMembershipSku = oneYearMembership?.variants[0]?.sku ?? '';
  const twoYearWarrantySku = twoYearWarranty?.variants[0]?.sku ?? '';
  const threeYearWarrantySku = threeYearWarranty?.variants[0]?.sku ?? '';

  return {
    product,
    subscription,
    oneYearMembership,
    twoYearWarranty,
    threeYearWarranty,
    ringSizesData,
    sizeLaterData,
    oneMonthMembershipSku,
    oneYearMembershipSku,
    twoYearWarrantySku,
    threeYearWarrantySku,
  };
};

export default getProduct;
